import { lazy, Suspense, memo, useState, useEffect, useCallback } from "react";
import { Bot } from "lucide-react";

interface DraggableKaiProps {
  customName?: string;
  showSpeech?: boolean;
  skillContext?: string;
}

// Lazy load the heavy DraggableKai component
const DraggableKaiInner = lazy(() => import("./DraggableKai").then(m => ({ default: m.DraggableKai })));

// Lightweight 2D fallback for mobile/weak devices
const KaiFallback2D = memo(({ customName = "Kai" }: { customName?: string }) => (
  <div className="fixed bottom-20 right-4 md:bottom-24 md:right-8 z-50">
    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg cursor-pointer hover:scale-105 transition-transform">
      <Bot className="w-7 h-7 text-white" />
    </div>
    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
  </div>
));
KaiFallback2D.displayName = "KaiFallback2D";

// Simple placeholder while loading
const KaiPlaceholder = () => (
  <div className="fixed bottom-20 right-4 md:bottom-24 md:right-8 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-primary/50 to-secondary/50 animate-pulse" />
);

// Check if device can handle 3D - ALWAYS allow 3D, only check for reduced motion
const canHandle3D = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Only check for reduced motion preference - respect accessibility
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    return false;
  }
  
  return true;
};

// Wrapper that loads immediately when mounted - NO DELAYS
export const DraggableKaiLazy = memo(({ customName = "Kai", showSpeech = true, skillContext }: DraggableKaiProps) => {
  const [use3D, setUse3D] = useState(() => canHandle3D());
  const [loadError, setLoadError] = useState(false);
  const [isReady, setIsReady] = useState(true); // Immediately ready
  
  // Check visibility setting - default is OFF
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    const setting = localStorage.getItem("kai_ai_visible");
    // Default is false (disabled) - only true if explicitly set
    return setting === "true";
  });

  useEffect(() => {
    const handleStorage = () => {
      const setting = localStorage.getItem("kai_ai_visible");
      setIsVisible(setting !== "false");
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Error handler for 3D component
  const handleError = useCallback(() => {
    console.warn('3D Kai failed to load, falling back to 2D');
    setLoadError(true);
  }, []);

  if (!isVisible) return null;
  
  // If there was an error or device can't handle 3D, show 2D fallback
  if (loadError || !use3D) {
    return <KaiFallback2D customName={customName} />;
  }

  // Render immediately - no placeholder delays
  return (
    <Suspense fallback={<KaiFallback2D customName={customName} />}>
      <ErrorBoundaryKai onError={handleError}>
        <DraggableKaiInner customName={customName} showSpeech={showSpeech} skillContext={skillContext} />
      </ErrorBoundaryKai>
    </Suspense>
  );
});

DraggableKaiLazy.displayName = "DraggableKaiLazy";

// Simple error boundary for Kai component
import { Component, ReactNode, ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  onError: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundaryKai extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Kai 3D Error:', error, errorInfo);
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return null; // Parent will show 2D fallback
    }
    return this.props.children;
  }
}
